<template>
  <div class="text-center">
    <upload-logo :height="128" :width="128" :url="project.logo_url" @upload="logo_file_id = $event" />

    <a-form-model
      layout="horizontal"
      ref="form"
      class="flex gap-4 text-left"
      :model="form"
      :rules="rules"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
    >
      <div class="flex flex-column gap-3">
        <div class="flex flex-column gap-2">
          <div class="flex gap-2 flex-wrap justify-content-between align-items-center">
            <h3>Company</h3>
          </div>

          <div class="grid">
            <div class="col-4">
              <b>Staff Company / Legal Entity</b>
              <p>
                Work with Clients to send invoices, share documents about your team, and maintain professional
                relationships.
              </p>
            </div>
            <div class="col-8">
              <a-switch v-model="form.staffing" :disabled="hasLinkedLegalEntity" />
            </div>
          </div>
          <a-form-model-item :colon="false" label-align="left" label="Name" prop="name">
            <a-input v-model="form.name" placeholder="Name" autocomplete="off" />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Short Name" prop="short_name">
            <a-input v-model="form.short_name" placeholder="Short Name" autocomplete="off" />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Description" prop="description">
            <a-textarea v-model="form.description" placeholder="Description" />
          </a-form-model-item>
          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Employer Tax ID (EIN)"
            prop="tax_id"
            :help="taxIdExtraText"
          >
            <a-input v-model="form.tax_id" :masked="true" raw v-mask="'##-#######'" placeholder="Employer Tax ID (EIN)"
              ><a-icon v-show="loadingTaxId || loadingProject" slot="suffix" type="loading"
            /></a-input>
          </a-form-model-item>
          <a-form-model-item :colon="false" label-align="left" label="Payroll Tax ID" prop="payroll_tax_id">
            <a-input
              v-model="form.payroll_tax_id"
              :masked="true"
              raw
              v-mask="'##-#######'"
              placeholder="Payroll Tax ID"
            />
          </a-form-model-item>
          <a-form-model-item :colon="false" label-align="left" label="Owner">
            <a-input v-model="form.owner" placeholder="Owner" autocomplete="off" :disabled="true" />
          </a-form-model-item>
          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Join Type"
            prop="join_type"
            :extra="joinTypesList.find(item => item.id === form.join_type).extra"
          >
            <custom-select
              :multiple="false"
              :allowClear="false"
              :value.sync="form.join_type"
              placeholder="Join Type"
              :options="joinTypesList"
              name="name"
              optionKey="id"
            />
          </a-form-model-item>
          <a-form-model-item :colon="false" label-align="left" label="Tags">
            <a-textarea v-model="form.tags" placeholder="Tags" />
          </a-form-model-item>
          <a-form-model-item :colon="false" label-align="left" label="Time Zone" prop="timezone">
            <custom-select
              :multiple="false"
              :allowClear="false"
              :value.sync="form.timezone"
              placeholder="Time Zone"
              :options="timeZones"
              name="name"
              optionKey="id"
            />
          </a-form-model-item>
        </div>
        <div class="flex flex-column gap-2">
          <h3>Address</h3>
          <a-form-model-item :colon="false" label-align="left" label="Address Line 1" prop="address.address_line1">
            <a-input v-model="form.address.address_line1" placeholder="Enter Address" />
          </a-form-model-item>
          <a-form-model-item :colon="false" label-align="left" label="Address Line 2" prop="address.address_line2">
            <a-input v-model="form.address.address_line2" placeholder="Enter Address" />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="City" prop="address.city">
            <a-input v-model="form.address.city" placeholder="Enter City" />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Country" prop="address.country">
            <custom-select
              :multiple="false"
              :value.sync="form.address.country"
              placeholder="Enter Country"
              :options="countriesList"
              name="name"
              optionKey="code"
            />
          </a-form-model-item>

          <a-form-model-item
            v-if="form.address.country === 'US' || form.address.country === 'us'"
            :colon="false"
            label-align="left"
            label="State"
            prop="address.state"
          >
            <custom-select
              :multiple="false"
              :value.sync="stateProxy"
              placeholder="Enter State"
              :options="statesList"
              name="name"
              optionKey="code"
            />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Zipcode" prop="address.zipcode">
            <a-input v-model="form.address.zipcode" placeholder="Enter Zipcode" />
          </a-form-model-item>
          <h3>Contacts</h3>
          <a-form-model-item :colon="false" label-align="left" label="Email" prop="email">
            <a-input v-model="form.email" placeholder="Email" autocomplete="off" />
          </a-form-model-item>
          <a-form-model-item :colon="false" label-align="left" label="Phone" prop="phone">
            <phone-input
              @input="
                value => {
                  $refs.form.validateField('phone')
                  form.phone = value
                }
              "
              :value="form.phone"
            />
          </a-form-model-item>

          <h3>Business Hours</h3>
          <p class="business-hours-timezone">Time Zone: {{ businessHoursTimezone ?? form.timezone ?? '-' }}</p>

          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Weekdays"
            :validate-status="businessHoursErrors.weekdays ? 'error' : undefined"
            :help="businessHoursErrors.weekdays"
          >
            <div class="business-hours-range">
              <a-time-picker
                class="business-hours-picker"
                :value="businessHoursTimeValue('weekdays', 'start')"
                :default-open-value="moment('09:00', 'HH:mm')"
                :disabled="loadingBusinessHours || loading"
                format="HH:mm"
                placeholder="Start"
                @change="value => handleBusinessHoursChange('weekdays', 'start', value)"
              />
              <span>to</span>
              <a-time-picker
                class="business-hours-picker"
                :value="businessHoursTimeValue('weekdays', 'end')"
                :default-open-value="moment('17:00', 'HH:mm')"
                :disabled="loadingBusinessHours || loading"
                format="HH:mm"
                placeholder="End"
                @change="value => handleBusinessHoursChange('weekdays', 'end', value)"
              />
            </div>
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Weekends"
            :validate-status="businessHoursErrors.weekends ? 'error' : undefined"
            :help="businessHoursErrors.weekends"
          >
            <div class="business-hours-range">
              <a-time-picker
                class="business-hours-picker"
                :value="businessHoursTimeValue('weekends', 'start')"
                :default-open-value="moment('09:00', 'HH:mm')"
                :disabled="loadingBusinessHours || loading"
                format="HH:mm"
                placeholder="Start"
                @change="value => handleBusinessHoursChange('weekends', 'start', value)"
              />
              <span>to</span>
              <a-time-picker
                class="business-hours-picker"
                :value="businessHoursTimeValue('weekends', 'end')"
                :default-open-value="moment('17:00', 'HH:mm')"
                :disabled="loadingBusinessHours || loading"
                format="HH:mm"
                placeholder="End"
                @change="value => handleBusinessHoursChange('weekends', 'end', value)"
              />
            </div>
          </a-form-model-item>
        </div>
        <div class="flex flex-column gap-2">
          <h3>Company Policy</h3>
          <div v-if="project.project_policy_type == 'text'">
            {{ project.project_policy_text ?? 'Company policy is not available' }}
          </div>
          <div v-else-if="project.project_policy_type == 'pdf'">
            <a-button icon="download" @click="downloadPolicy"> Download Policy </a-button>
          </div>
        </div>
        <div class="flex gap-2 justify-content-end mb-3">
          <a-button type="danger" @click="resetForm"> Reset </a-button>
          <a-button
            type="primary"
            :loading="loading"
            :disabled="loadingTaxId || loadingProject || loadingBusinessHours"
            @click="handleSubmit"
          >
            Save
          </a-button>
        </div>
      </div>
    </a-form-model>
  </div>
</template>

<script>
import { notification, Switch, Button, Input, FormModel, Icon, TimePicker } from 'ant-design-vue'
import { vueTourOptions } from '@/const'
import permissionMixin from '@/mixins/permissionMixin'
import customSelectVue from '../../../components/ui/custom-select.vue'
import { deepCopy } from '@/utils/deep-copy'
import constData from '@/helpers/const-data'
import { debounce } from '@/helpers/debounce'
import { createNamespacedHelpers } from 'vuex'
import moment from 'moment-timezone'
import UploadLogo from '../components/upload-logo.vue'
import PhoneInput from '@/components/ui/phone-input.vue'

const { mapActions: legalEntityActions } = createNamespacedHelpers('legalEntity')
const { mapActions: projectActions } = createNamespacedHelpers('project')

const BUSINESS_HOURS_GROUPS = Object.freeze({
  weekdays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  weekends: ['saturday', 'sunday']
})

const emptyBusinessHours = () => ({
  weekdays: { start: null, end: null },
  weekends: { start: null, end: null }
})

const initialForm = {
  name: undefined,
  short_name: undefined,
  description: null,
  email: undefined,
  phone: undefined,
  tax_id: null,
  payroll_tax_id: null,
  join_type: undefined,
  tags: null,
  timezone: undefined,
  auto_approve_time_off: false,
  address: {
    address_line1: null,
    address_line2: null,
    city: null,
    state: null,
    zipcode: null,
    country: null
  }
}

const initialRules = {
  name: [
    {
      required: true,
      message: 'Please enter the company name!'
    },
    {
      min: 2,
      max: 100,
      message: 'The name must be between 2 and 100 characters.'
    }
  ],
  short_name: [
    {
      required: true,
      message: 'Please enter the company short name!'
    },
    {
      min: 2,
      max: 12,
      message: 'The short name must be between 2 and 12 characters.'
    }
  ],
  email: [
    {
      required: true,
      message: 'Please enter the email address!'
    },
    {
      type: 'email',
      message: 'The input is not a valid email!'
    }
  ],
  phone: [
    {
      required: true,
      message: 'Phone can not be empty',
      trigger: 'change'
    },
    {
      transform: value => (value ? value.replace(/\s+/g, '') : value),
      pattern: /^\+\d{7,18}$/,
      message: 'Invalid phone number. It should start with a + and contain 10 to 15 digits.'
    }
  ],
  join_type: [
    {
      required: true,
      message: 'Please select a join type!'
    }
  ],
  address: {
    zipcode: [
      {
        min: 5,
        message: 'The zipcode must be more than 4 characters.',
        trigger: 'change'
      }
    ],
    country: [
      {
        required: true,
        message: 'Country can not be empty',
        trigger: 'change'
      }
    ]
  }
}

export default {
  components: {
    'a-button': Button,
    'a-switch': Switch,
    'a-input': Input,
    'a-icon': Icon,
    'a-textarea': Input.TextArea,
    'a-time-picker': TimePicker,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'custom-select': customSelectVue,
    'upload-logo': UploadLogo,
    'phone-input': PhoneInput
  },
  mixins: [permissionMixin],
  data() {
    return {
      loading: false,
      loadingTaxId: false,
      loadingProject: false,
      loadingBusinessHours: false,

      logo_file_id: undefined,

      form: deepCopy(initialForm),
      businessHours: emptyBusinessHours(),
      initialBusinessHours: emptyBusinessHours(),
      businessHoursTimezone: null,
      businessHoursErrors: {
        weekdays: '',
        weekends: ''
      },
      rules: {
        ...initialRules,
        tax_id: [
          {
            len: 9,
            message: 'Tax ID must be contain 9 digit',
            trigger: 'change',
            transform: value => (value ? value.replace(/\D/g, '') : value)
          },
          {
            validator: this.taxIdValidator,
            trigger: 'change'
          }
        ]
      },

      joinTypesList: [
        {
          name: 'Open',
          id: 'open',
          extra:
            'Anyone can find your company and join without additional verification or an invitation. You can also invite anyone.'
        },
        {
          name: 'Request or Invite',
          id: 'request',
          extra:
            'Anyone can request to join your company. After you approve the request, you can accept them. You can also invite anyone to hire them.'
        },
        {
          name: 'Invite Only',
          id: 'invite',
          extra: 'Your company is hidden from search, but you can invite anyone to hire them.'
        }
      ],

      countriesList: constData.countries,
      statesList: constData.usStates,
      timeZones: moment.tz.names().map(item => ({ name: item, id: item })),

      legalEntities: [],

      debouncedTaxIdValidator: null
    }
  },
  watch: {
    'form.address.country': {
      handler(newVal) {
        if (newVal && newVal.toUpperCase() !== 'US') {
          this.form.address.state = undefined
        }
      }
    },
    'form.tax_id': {
      handler: debounce(async function (newVal) {
        if (!newVal) {
          this.legalEntities = []
          return
        }

        this.loadingTaxId = true

        try {
          const result = await this.getLegalEntityByTaxId({
            tax_id: newVal.replace(/\D/g, '')
          })

          this.legalEntities = result
        } catch (error) {
          this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        } finally {
          this.loadingTaxId = false
        }
      }, 300)
    },
    project: {
      handler(newVal) {
        if (newVal) {
          this.form = deepCopy(this.project)
        }
      },
      immediate: true
    },
    'project.id': {
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.loadBusinessHours()
        }
      },
      immediate: true
    }
  },
  computed: {
    vueTourOptions() {
      return vueTourOptions
    },

    project() {
      return this.$store.state.applicationState.currentProject
    },

    projectLogoUrlAvailable() {
      return this.project.logo_url != null
    },

    disableActions() {
      return JSON.parse(JSON.stringify(this.form)) === JSON.parse(JSON.stringify(this.project))
    },

    businessHoursChanged() {
      return JSON.stringify(this.businessHours) !== JSON.stringify(this.initialBusinessHours)
    },

    stateProxy: {
      get() {
        return this.form.address.state ? this.form.address.state.toUpperCase() : undefined
      },
      set(value) {
        this.form.address.state = value
      }
    },

    countryProxy: {
      get() {
        return this.form.address.country ? this.form.address.country.toUpperCase() : undefined
      },
      set(value) {
        this.form.address.country = value
      }
    },

    taxIdExtraText() {
      let connectedClients = 0
      let potentialClients = 0

      ;(this.legalEntities ?? []).forEach(item => {
        if (item.staffing_status === 'linked') {
          connectedClients += 1
        } else {
          potentialClients += 1
        }
      })

      return `Your company is connected with ${connectedClients} clients and has ${potentialClients} pending clients`
    },

    hasLinkedLegalEntity() {
      return (this.legalEntities ?? []).some(item => item.staffing_status === 'linked')
    }
  },
  methods: {
    ...legalEntityActions(['getLegalEntityByTaxId']),
    ...projectActions(['getProjectByTaxId', 'patchUpdateProject', 'getBusinessHours', 'saveBusinessHours']),

    moment: moment,

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    async loadBusinessHours() {
      const projectId = this.project.id

      if (!projectId) return

      this.loadingBusinessHours = true

      try {
        const data = await this.getBusinessHours({ project_id: projectId })

        if (this.project.id === projectId) {
          this.applyBusinessHoursResponse(data)
        }
      } catch (error) {
        this.showNotification('error', 'Error', error?.message ?? error)
      } finally {
        if (this.project.id === projectId) {
          this.loadingBusinessHours = false
        }
      }
    },

    applyBusinessHoursResponse(data) {
      const hours = data.hours ?? {}
      const nextBusinessHours = Object.entries(BUSINESS_HOURS_GROUPS).reduce((result, [group, days]) => {
        const groupHours = hours[days[0]] ?? {}

        result[group] = {
          start: groupHours.start ?? null,
          end: groupHours.end ?? null
        }

        return result
      }, {})

      this.businessHours = nextBusinessHours
      this.initialBusinessHours = deepCopy(nextBusinessHours)
      this.businessHoursTimezone = data.timezone ?? null
      this.businessHoursErrors = { weekdays: '', weekends: '' }
    },

    businessHoursTimeValue(group, field) {
      const value = this.businessHours[group][field]

      return value ? moment(value, 'HH:mm', true) : null
    },

    handleBusinessHoursChange(group, field, value) {
      this.businessHours[group][field] = value ? value.format('HH:mm') : null

      if (this.businessHoursErrors[group]) {
        this.validateBusinessHoursGroup(group)
      }
    },

    validateBusinessHoursGroup(group) {
      const { start, end } = this.businessHours[group]
      const valid = Boolean(start) === Boolean(end)

      this.businessHoursErrors[group] = valid ? '' : 'Select both start and end time.'
      return valid
    },

    validateBusinessHours() {
      return Object.keys(BUSINESS_HOURS_GROUPS)
        .map(group => this.validateBusinessHoursGroup(group))
        .every(Boolean)
    },

    serializeBusinessHours() {
      return Object.entries(BUSINESS_HOURS_GROUPS).reduce((result, [group, days]) => {
        days.forEach(day => {
          result[day] = { ...this.businessHours[group] }
        })

        return result
      }, {})
    },

    taxIdValidator: debounce(async function (rule, value, callback) {
      try {
        if (!value) {
          return callback()
        }

        this.loadingProject = true

        const result = await this.getProjectByTaxId({
          tax_id: value.replace(/\D/g, '')
        })

        if (result && result.id !== this.project.id) {
          callback(
            new Error(
              `The specified Tax ID is ALREADY in USE in one of your Company. Please double-check the information! After that, try again!`
            )
          )
        } else {
          return callback()
        }
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingProject = false
      }
    }, 300),

    resetForm() {
      this.form = deepCopy(this.project)
      this.businessHours = deepCopy(this.initialBusinessHours)
      this.businessHoursErrors = { weekdays: '', weekends: '' }
    },

    handleSubmit() {
      this.$refs.form.validate(async valid => {
        const businessHoursValid = this.validateBusinessHours()

        if (!valid || !businessHoursValid) {
          this.showNotification(
            'info',
            'Info',
            'Some fields are not filled in or are filled in incorrectly. Please check the fields'
          )

          return
        }

        this.loading = true

        try {
          const projectId = this.project.id
          const timezoneChanged = this.form.timezone !== this.project.timezone
          const businessHoursChanged = this.businessHoursChanged
          const payload = {
            project: {
              id: projectId,
              schedule_integration_url: this.project.schedule_integration_url,
              ...this.form,
              tax_id: this.form.tax_id ? this.form.tax_id.replace(/\D/g, '') : undefined,
              email: this.form.email.toLowerCase()
            },
            logo_file_id: this.logo_file_id
          }

          await this.patchUpdateProject(payload)

          if (businessHoursChanged) {
            const data = await this.saveBusinessHours({
              project_id: projectId,
              hours: this.serializeBusinessHours()
            })

            this.applyBusinessHoursResponse(data)
          } else if (timezoneChanged) {
            const data = await this.getBusinessHours({ project_id: projectId })
            this.applyBusinessHoursResponse(data)
          }

          this.showNotification('success', 'Success', `Project has been updated successfully`)
        } catch (error) {
          this.showNotification('error', 'Error', error?.message ?? error)
        } finally {
          this.loading = false
        }
      })
    },

    downloadPolicy() {
      window.open(this.project.project_policy_text, '_blank')
    }
  }
}
</script>

<style scoped>
.content-view-block {
  width: 365px;
}

.loading-spin-project-info {
  margin-top: 40px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.project-name-with-logo-container {
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.project-name {
  margin: unset;
}

.logo-container {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  cursor: pointer;
  overflow: hidden;
}

.camera-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 24px;
  color: white;
  opacity: 0;
  background-color: rgba(0, 0, 0, 0);
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition:
    opacity 0.3s ease-in-out,
    background-color 0.3s ease-in-out;
}

.logo-container:hover .camera-icon {
  opacity: 1;
  background-color: rgba(0, 0, 0, 0.5);
}

.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}

.business-hours-timezone {
  margin: 0;
}

.business-hours-range {
  display: flex;
  align-items: center;
  gap: 8px;
}

.business-hours-picker {
  flex: 1;
  min-width: 0;
}
</style>
